import moment from 'moment'
import { TimeInterval } from 'types/search/chart'

/**
 * Calculate the appropriate time interval based on the date range
 *
 * Rules:
 * - x <= 1 min: ONE_SECOND
 * - 1 min < x <= 10 min: TEN_SECONDS
 * - 10 min < x <= 1h: ONE_MINUTE
 * - 1h < x <= 6h: TEN_MINUTES
 * - 6h < x <= 2d: ONE_HOUR
 * - 2d < x <= 7d: THREE_HOURS
 * - x > 7d: ONE_DAY
 *
 * @param startDate - Start date in ISO format
 * @param endDate - End date in ISO format (if null, uses current time)
 * @returns TimeInterval enum value
 */
export const calculateTimeInterval = (
  startDate: string | null,
  endDate: string | null,
): TimeInterval => {
  // If no start date, default to ONE_SECOND
  if (!startDate) {
    return TimeInterval.ONE_SECOND
  }

  // Calculate the time difference in milliseconds
  const start = moment(startDate)
  const end = endDate ? moment(endDate) : moment()
  const diffMs = end.diff(start)

  // Convert to minutes for easier comparison
  const diffMinutes = diffMs / (60 * 1000)

  // Apply the rules
  if (diffMinutes <= 1) {
    return TimeInterval.ONE_SECOND
  } else if (diffMinutes <= 10) {
    // 1 min < x <= 10 min: TEN_SECONDS
    return TimeInterval.TEN_SECONDS
  } else if (diffMinutes <= 60) {
    // 10 min < x <= 1h: ONE_MINUTE
    return TimeInterval.ONE_MINUTE
  } else if (diffMinutes <= 360) {
    // 1h < x <= 6h: TEN_MINUTES
    return TimeInterval.TEN_MINUTES
  } else if (diffMinutes <= 2880) {
    // 6h < x <= 2d (2880 minutes = 2 days)
    return TimeInterval.ONE_HOUR
  } else if (diffMinutes <= 10080) {
    // 2d < x <= 7d (10080 minutes = 7 days)
    return TimeInterval.THREE_HOURS
  } else {
    // x > 7d: ONE_DAY
    return TimeInterval.ONE_DAY
  }
}
